import { ReactNode, useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NAV, CORE_NAV } from '../utils/nav';
import { recordUsage } from '../utils/usage';
import { MODULE_KEYS, hasModule } from '../utils/modules';
import { certificateApi } from '../api';

export default function Layout({ children }: { children: ReactNode }) {
  const { user, logout, currentCustomer, backToOrg } = useAuth();
  const navigate = useNavigate();
  const [certAlert, setCertAlert] = useState(0);

  // 一级（未进入账套）：只显示 账套 + 其他功能，二级核心功能不在主页面显示
  // 二级（进入账套后）：只显示核心功能（凭证/账簿/财务报表/结账/固定资产/发票/设置），一级功能不再显示
  const inCustomer = !!currentCustomer;
  const corePaths = new Set(CORE_NAV.map((n) => n.to));
  const perms = (user as any)?.permissions ?? null;
  const allowed = (to: string) => {
    const key = Object.keys(MODULE_KEYS).find((k) => MODULE_KEYS[k].path === to);
    if (!key) return true; // 非受控模块（账套/首页/用户管理）始终显示
    return hasModule(perms, key);
  };
  const orgItems = NAV.filter((n) => !corePaths.has(n.to) && allowed(n.to));
  const coreItems = CORE_NAV.filter((n) => allowed(n.to));

  // 证照到期提醒：一级界面加载时取一次统计，未授权员工 403 时静默忽略；页面内增删后经 cert-alert 事件实时同步
  useEffect(() => {
    if (inCustomer) return;
    if (!allowed('/certificates')) return;
    certificateApi.summary()
      .then((s: any) => setCertAlert((s.expired || 0) + (s.expiring || 0)))
      .catch(() => {});
    const onAlert = (e: Event) => setCertAlert(Number((e as CustomEvent).detail) || 0);
    window.addEventListener('cert-alert', onAlert);
    return () => window.removeEventListener('cert-alert', onAlert);
  }, [inCustomer, user?.id]);

  const linkCls = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 px-4 py-2.5 text-sm whitespace-nowrap transition-colors ${
      isActive ? 'bg-primary text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
    }`;

  const renderItem = ({ to, label, icon: Icon }: { to: string; label: string; icon: any }) => (
    <NavLink key={to} to={to} onClick={() => recordUsage(user?.id, to)} className={linkCls}>
      <Icon size={16} /> <span className="flex-1">{label}</span>
      {to === '/certificates' && certAlert > 0 && (
        <span className="rounded-full bg-red-500 text-white text-[11px] leading-4 px-1.5 min-w-4 text-center">{certAlert > 99 ? '99+' : certAlert}</span>
      )}
    </NavLink>
  );

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <aside className="w-full md:w-52 shrink-0 bg-[hsl(215_28%_17%)] text-white flex flex-col">
        <div className="px-4 py-4 border-b border-white/10">
          <div className="text-[15px] font-semibold">樾快工作台</div>
          {currentCustomer && (
            <button
              className="mt-2 w-full flex items-center justify-between rounded-sm bg-white/10 px-2 py-1.5 text-xs text-white hover:bg-white/20"
              onClick={async () => { await backToOrg(); navigate('/customers'); }}
            >
              <span className="truncate">当前账套：{currentCustomer}</span>
              <span>返回</span>
            </button>
          )}
        </div>
        <nav className="flex-1 py-2 flex md:flex-col overflow-x-auto md:overflow-visible">
          {inCustomer ? (
            <>
              <div className="px-4 pt-1 pb-1.5 text-[11px] text-white/40">核心功能</div>
              {coreItems.map(renderItem)}
            </>
          ) : (
            orgItems.map(renderItem)
          )}
        </nav>
        <div className="border-t border-white/10 p-3 text-xs">
          <div className="truncate text-white/80">{user?.name || user?.phone}</div>
          <button
            className="mt-2 flex items-center gap-1.5 text-white/60 hover:text-white"
            onClick={() => { logout(); navigate('/login'); }}
          >
            <LogOut size={14} /> 退出登录
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-4 md:p-6">{children}</main>
    </div>
  );
}
